'use client';

import {
  createContext,
  use,
  useLayoutEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  DEFAULT_THEME,
  LEGACY_THEME_STORAGE_KEY,
  THEME_STORAGE_KEY,
} from '@/lib/theme-config';

type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
  forcedTheme?: Theme;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

function readStoredTheme(storageKey: string, defaultTheme: Theme): Theme {
  if (typeof window === 'undefined') return defaultTheme;
  const stored =
    (localStorage.getItem(storageKey) as Theme | null) ||
    (localStorage.getItem(LEGACY_THEME_STORAGE_KEY) as Theme | null);
  return stored || defaultTheme;
}

function resolveTheme(theme: Theme, forcedTheme?: Theme): 'dark' | 'light' {
  if (forcedTheme && forcedTheme !== 'system') return forcedTheme;
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
}

function applyThemeClass(theme: Theme, forcedTheme?: Theme) {
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(resolveTheme(theme, forcedTheme));
}

export function ThemeProvider({
  children,
  defaultTheme = DEFAULT_THEME as Theme,
  storageKey = THEME_STORAGE_KEY,
  forcedTheme,
  ...props
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (forcedTheme) return forcedTheme;
    return readStoredTheme(storageKey, defaultTheme);
  });

  useLayoutEffect(() => {
    applyThemeClass(theme, forcedTheme);
  }, [theme, forcedTheme]);

  useLayoutEffect(() => {
    if (forcedTheme || theme !== 'system') return;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyThemeClass('system', forcedTheme);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [theme, forcedTheme]);

  const value = {
    theme,
    setTheme: (next: Theme) => {
      if (forcedTheme) return;
      localStorage.setItem(storageKey, next);
      setThemeState(next);
      applyThemeClass(next, forcedTheme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = use(ThemeProviderContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};
